import { getIntegration } from "@/features/integrations/catalog";
import { useIntegrationName } from "@/features/integrations/hooks/use-integration-name";
import { cn } from "@/lib/utils";
import { PlugsIcon } from "@phosphor-icons/react";

const SIZES = {
  xs: { tile: "h-6 w-6 rounded", inner: "h-3.5 w-3.5" },
  sm: { tile: "h-8 w-8 rounded-md", inner: "h-4 w-4" },
  md: { tile: "h-10 w-10 rounded-lg", inner: "h-5 w-5" },
  lg: { tile: "h-12 w-12 rounded-xl", inner: "h-7 w-7" },
} as const;

// Spelled out rather than built (`bg-${tint}-…`): Tailwind only ships the
// classes it can read in the source.
const TINTS: Record<string, string> = {
  primary: "bg-primary/10 text-primary",
  violet:
    "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  sky: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
};

/**
 * The logo of an integration (`features/integrations/catalog.ts`) on a square
 * tile. A brand sits on white in both themes — its colours are its own — and an
 * unknown id gets a neutral plug rather than nothing, so a provider added on the
 * server before the front knows it still lines up with the others.
 */
export function IntegrationLogo({
  id,
  size = "md",
  className,
}: {
  id: string | undefined | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const integration = getIntegration(id);
  const { tile, inner } = SIZES[size];

  if (integration && "src" in integration.logo) {
    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center border bg-white",
          tile,
          className,
        )}
      >
        {/* Decorative: the name is always written next to it. */}
        <img src={integration.logo.src} alt="" className={inner} />
      </div>
    );
  }

  const Icon =
    integration && "icon" in integration.logo
      ? integration.logo.icon
      : PlugsIcon;
  const tint =
    integration && "tint" in integration.logo
      ? TINTS[integration.logo.tint]
      : "bg-muted text-muted-foreground";

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center",
        tile,
        tint,
        className,
      )}
      aria-hidden
    >
      <Icon weight="duotone" className={inner} />
    </div>
  );
}

/**
 * The integration's logo and name in a line of text — where a tile would read as
 * a control (ElevenLabs' two bars beside a play button are a pause button).
 */
export function IntegrationName({
  id,
  className,
}: {
  id: string | undefined | null;
  className?: string;
}) {
  const integration = getIntegration(id);
  const name = useIntegrationName()(id);
  if (!name) return null;
  const Icon =
    integration && "icon" in integration.logo
      ? integration.logo.icon
      : PlugsIcon;

  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      {integration && "src" in integration.logo ? (
        <img
          src={integration.logo.src}
          alt=""
          className="h-3 w-3 dark:rounded-[2px] dark:bg-white dark:p-px"
        />
      ) : (
        <Icon weight="duotone" className="h-3.5 w-3.5" aria-hidden />
      )}
      {name}
    </span>
  );
}
